import { Lightbulb } from "lucide-react";
import { useState, type ReactNode } from "react";
import useTranslation from "../../../client/hooks/useTranslation";
import type { SessionStats } from "../../../trading-journal/analytics";
import { getMoodEmoji, getMoodLabel } from "../../../trading-sessions/sessionUtils";
import { useLiteData } from "../../data/LiteDataProvider";
import { pnlClass } from "../../lib/format";
import { PnlBars } from "../../ui/Charts";
import { Bar, Card, Empty, Segmented, Stat, toneColor } from "../../ui/ui";
import type { SessionRow } from "./sessionData";

type Tab = "mood" | "duration" | "timing" | "bySession" | "outside";

/**
 * Les cinq widgets d'analyse de l'Advanced (humeur, durée, créneaux, P&L par
 * session, hors session) et l'enseignement clé, réunis en une carte à onglets.
 * Les chiffres viennent de calculateSessionStats, comme dans l'Advanced.
 */
export function SessionAnalysis({ stats, rows }: { stats: SessionStats; rows: SessionRow[] }) {
  const t = useTranslation();
  const ta = t.analytics;
  const tl = t.lite.sessions.analysis;
  const { fmt } = useLiteData();
  const [tab, setTab] = useState<Tab>("mood");

  // Mêmes libellés que les pastilles d'humeur de la liste.
  const moodLabel = (m: string) => getMoodLabel(m, t);

  // Même phrase que le widget « Enseignement clé », montants au format du Lite.
  const ins = stats.insight;
  const insight = !stats.hasEnoughData ? null
    : ins.type === "timeBucket" ? ta.sessionInsightTimeBucket.replace("{pct}", String(ins.pct ?? 0)).replace("{bucket}", ins.bucketLabel ?? "")
    : ins.type === "mood" ? ta.sessionInsightMood.replace("{mood}", moodLabel(ins.mood ?? "")).replace("{avg}", fmt.money(ins.avgPnl ?? 0))
    : ins.type === "duration" ? ta.sessionInsightDuration.replace("{dur}", ins.durationLabel ?? "").replace("{avg}", fmt.money(ins.avgPnl ?? 0))
    : null;

  const chronological = [...rows].reverse();
  const os = stats.outsideSession;
  const notEnough = <Empty title={ta.notEnoughSessionData} />;

  return (
    <Card title={tl.title} bodyClassName="space-y-4">
      {insight && (
        <div className="flex items-start gap-2.5 rounded-xl px-3 py-2.5 text-sm" style={{ background: toneColor("brand", 0.1) }}>
          <Lightbulb size={16} className="text-primary shrink-0 mt-0.5" aria-label={ta.sessionInsightLabel} />
          <span className="text-foreground">{insight}</span>
        </div>
      )}

      <Segmented<Tab>
        size="sm"
        value={tab}
        onChange={setTab}
        options={(["mood", "duration", "timing", "bySession", "outside"] as Tab[]).map(id => ({ id, label: tl.tabs[id] }))}
      />

      {tab === "mood" && (
        <Section desc={ta.sessionStatsMoodDesc}>
          {stats.moodStats.length ? (
            <AvgRows rows={stats.moodStats.map(m => ({ key: m.mood, label: `${getMoodEmoji(m.mood)} ${moodLabel(m.mood)}`, value: m.avgPnl, count: m.sessionCount }))} />
          ) : notEnough}
        </Section>
      )}

      {tab === "duration" && (
        <Section desc={ta.sessionStatsDurationDesc}>
          {stats.durationBuckets.length ? (
            <AvgRows rows={stats.durationBuckets.map(b => ({ key: b.label, label: b.label, value: b.avgPnl, count: b.sessionCount }))} />
          ) : notEnough}
        </Section>
      )}

      {tab === "timing" && (
        <Section desc={ta.sessionStatsTimeDesc}>
          {stats.hasEnoughData && stats.timeInSessionBuckets.some(b => b.tradeCount > 0) ? (
            <>
              <PnlBars data={stats.timeInSessionBuckets.map(b => ({ label: b.label, value: b.pnl }))} height={190} />
              <div>
                <div className="text-xs font-medium text-muted-foreground mb-2">{ta.sessionStatsWinRateTitle}</div>
                <div className="space-y-2">
                  {stats.timeInSessionBuckets.map(b => (
                    <div key={b.label} className="grid grid-cols-[88px_1fr_auto] items-center gap-3 text-xs">
                      <span className="text-muted-foreground truncate">{b.label}</span>
                      <Bar value={b.winRate / 100} tone={b.winRate >= 50 ? "gain" : "warn"} />
                      <span className="font-mono tabular-nums text-right w-[92px]">
                        {b.tradeCount ? `${fmt.pct(b.winRate / 100)} · ${b.tradeCount}` : "-"}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </>
          ) : notEnough}
        </Section>
      )}

      {tab === "bySession" && (
        <Section desc={t.dashboard.widgets.sessionPnlChart}>
          {chronological.length ? (
            <PnlBars data={chronological.map(r => ({ label: r.session.name || fmt.dateShort(r.session.startedAt!), value: r.pnl }))} height={220} />
          ) : notEnough}
        </Section>
      )}

      {tab === "outside" && (
        <Section desc={ta.outsideSessionDesc}>
          {os.tradeCount === 0 ? (
            <Empty title={stats.hasEnoughData ? ta.noDataAvailable : ta.outsideSessionNoSessions} />
          ) : (
            <div className="grid grid-cols-2 gap-x-6 gap-y-5">
              <Stat label={ta.outsideSessionTrades} value={os.tradeCount} hint={`${fmt.pct(os.pctOfTotalTrades / 100)} ${ta.outsideSessionPctTrades}`} />
              <Stat label={ta.outsideSessionWinRate} value={fmt.pct(os.winRate / 100)} hint={tl.wl(os.winCount, os.lossCount)} />
              <Stat label={ta.outsideSessionPnl} value={fmt.money(os.totalPnl)} tone={os.totalPnl > 0 ? "gain" : os.totalPnl < 0 ? "loss" : undefined} hint={tl.avgPerTrade(fmt.money(os.avgPnl))} />
              <Stat label={ta.outsideSessionProfitFactor} value={os.profitFactor === Infinity ? "∞" : fmt.num(os.profitFactor)} hint={tl.pctOfPnl(fmt.pct(os.pctOfTotalPnl / 100))} />
            </div>
          )}
        </Section>
      )}
    </Card>
  );
}

function Section({ desc, children }: { desc: string; children: ReactNode }) {
  return (
    <div className="space-y-3">
      <p className="text-xs text-muted-foreground">{desc}</p>
      {children}
    </div>
  );
}

/** Barres horizontales divergentes : P&L moyen par catégorie, avec le nombre de sessions. */
function AvgRows({ rows }: { rows: { key: string; label: string; value: number; count: number }[] }) {
  const tl = useTranslation().lite.sessions.analysis;
  const { fmt } = useLiteData();
  const max = Math.max(1, ...rows.map(r => Math.abs(r.value)));
  return (
    <div className="space-y-3">
      {rows.map(r => (
        <div key={r.key} className="space-y-1.5">
          <div className="flex items-baseline justify-between gap-3 text-sm">
            <span className="truncate">{r.label}</span>
            <span className="flex items-baseline gap-2 shrink-0">
              <span className="text-xs text-muted-foreground">{tl.sessionsCount(r.count)}</span>
              <span className={`font-mono tabular-nums font-medium ${pnlClass(r.value)}`}>{fmt.money(r.value)}</span>
            </span>
          </div>
          <div className="grid grid-cols-2 h-1.5 rounded-full bg-muted overflow-hidden">
            <div className="flex justify-end">{r.value < 0 && <div className="h-full bg-pnlNegative rounded-l-full" style={{ width: `${(Math.abs(r.value) / max) * 100}%` }} />}</div>
            <div>{r.value > 0 && <div className="h-full bg-pnlPositive rounded-r-full" style={{ width: `${(r.value / max) * 100}%` }} />}</div>
          </div>
        </div>
      ))}
    </div>
  );
}
